import { useState, useCallback, useEffect } from "react";

const INITIAL = { rotateX: 0, rotateY: 0, shineX: 50, shineY: 50 };

/**
 * 3D tilt effect on hover. Returns event handlers and transform style.
 * Only activates on devices with hover capability.
 * @param {number} maxDeg - Maximum rotation in degrees (default 5)
 */
export function useTilt(maxDeg = 5) {
  const [tilt, setTilt] = useState(INITIAL);
  const [canHover, setCanHover] = useState(false);

  useEffect(() => {
    setCanHover(window.matchMedia("(hover: hover)").matches);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!canHover) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setTilt({
        rotateX: (0.5 - y) * maxDeg * 2,
        rotateY: (x - 0.5) * maxDeg * 2,
        shineX: x * 100,
        shineY: y * 100,
      });
    },
    [canHover, maxDeg]
  );

  const handleMouseLeave = useCallback(() => {
    setTilt(INITIAL);
  }, []);

  const style = canHover
    ? {
        transform: `perspective(800px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg)`,
        transition: "transform 0.15s ease-out",
      }
    : {};

  const shineStyle = canHover
    ? {
        background: `radial-gradient(circle at ${tilt.shineX}% ${tilt.shineY}%, rgba(224,184,80,0.12) 0%, transparent 60%)`,
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        borderRadius: "inherit",
        zIndex: 15,
        opacity: tilt.rotateX === 0 && tilt.rotateY === 0 ? 0 : 1,
        transition: "opacity 0.3s ease",
      }
    : { display: "none" };

  return {
    handlers: { onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave },
    style,
    shineStyle,
  };
}
